import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Pencil, ConciergeBell } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["Concierge", "Housekeeping", "Dining", "Spa & Wellness", "Transport", "Facilities"];
const empty = { name: "", description: "", category: "Concierge", price: "", available: true };

export default function ServicesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/services").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, price: it.price ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    const payload = { ...form, price: form.price === "" ? null : parseFloat(form.price) };
    try {
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch { toast.error("Could not save"); }
  };

  const del = async (id) => { await api.delete(`/services/${id}`); toast.success("Removed"); load(); };

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Services</h1>
          <p className="text-muted-foreground text-sm">Bookable services your guests can request from their hub.</p>
        </div>
        <Button onClick={openNew} data-testid="add-service-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-2" /> Add service</Button>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-md">
          <ConciergeBell className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No services yet.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-md divide-y divide-border">
          {items.map((it) => (
            <div key={it.id} className="p-4 flex items-center justify-between" data-testid={`service-item-${it.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-foreground font-medium">{it.name}</p>
                  <Badge variant="secondary" className="text-xs">{it.category}</Badge>
                  {!it.available && <Badge className="bg-red-100 text-red-700 border-0 text-xs">Hidden</Badge>}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{it.description}</p>
              </div>
              <div className="flex items-center gap-4">
                {it.price != null && <span className="text-foreground font-serif">£{it.price}</span>}
                <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-service-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => del(it.id)} data-testid={`delete-service-${it.id}`} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Edit service" : "Add service"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input data-testid="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="service-category" className="mt-1.5 w-full h-10 rounded-md border border-input px-3 text-sm bg-background">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Description</Label><Textarea data-testid="service-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1.5" /></div>
            <div><Label>Price (£, optional)</Label><Input data-testid="service-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5" /></div>
            <div className="flex items-center justify-between">
              <Label>Visible to guests</Label>
              <Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="service-available" />
            </div>
            <Button onClick={save} data-testid="save-service-btn" className="w-full bg-primary text-primary-foreground">Save service</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
